import React, {useCallback, useState, useEffect, useMemo} from 'react'
import {useHistory} from 'react-router'
import * as ls from 'utils/localStorage';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {useActionApi} from 'hooks'
import Api from 'api'

import {HygienistBackendJob} from 'components/EditJob'
import {HygienistJobDetails} from 'components/HygienistJobDetails'
import {NoResumeActions} from 'components/HygienistJobDetails/NoResumeActions'
import {WithdrawActions} from 'components/HygienistJobDetails/WithdrawActions'
import {SuccessAction} from 'components/HygienistJobDetails/SuccessAction'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import TalentNavbar, { TalentPageTitle } from 'components/TalentNavbar';
import TalentPageLayout from 'components/TalentPageLayout';
import TalentBackButton from 'components/TalentBackButton/TalentBackButton';
import Popup from "components/Popup";
import { JobBoardApplySuccessModal } from 'components/Modal/JobBoardApplySuccessModal';
import {UserAccountProps, HYGIENIST_TYPES} from "utils/types";
import {CoverLetter} from './components/CoverLetter';
import type { WritingAssistSaveSelection } from 'components/WritingAssist';
import ApiError from 'api/ApiError';
import { submitWritingAssistApplication } from './writingAssistApplication';

dayjs.extend(utc);

// Compare against the user's local day rather than the current UTC day so
// "expired after 30 days" matches what the hygienist sees on their phone.
const isJobExpired = (startDate?: string): boolean => {
  if (!startDate) return false;
  const start = dayjs.utc(startDate);
  if (!start.isValid()) return false;
  const todayLocal = dayjs().startOf('day');
  return todayLocal.diff(start, 'day') > 30;
};

export default function JobPostDetailsPage() {
  const history = useHistory()

  const {job: initialJob}: { job: HygienistBackendJob } = history.location.state as any
  const alertSource = (history.location.state as any)?.alertSource;
  const [job, setJob] = useState<HygienistBackendJob>(initialJob);
  useEffect(() => {
    if (typeof alertSource === 'string' && /^alert-(inapp|push|email|sms)$/.test(alertSource)
      && Number.isSafeInteger(Number(initialJob?.id)) && Number(initialJob?.id) > 0) {
      Api.recordJobAlertInteraction('Job Alert Opened', {
        src: alertSource, jobPost: Number(initialJob.id),
      }).catch(error => console.error('[JobAlerts] opened event could not be recorded', error));
    }
  }, [alertSource, initialJob?.id]);
  const [hygienistUser, setHygienistUser] = useState<UserAccountProps | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [includeCoverLetter, setIncludeCoverLetter] = useState(true);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);
  const [temporaryCoverLetter, setTemporaryCoverLetter] = useState<string | null>(null);
  const [coverLetterPrepared, setCoverLetterPrepared] = useState(false);
  const [coverLetterProcessing, setCoverLetterProcessing] = useState(false);
  const [baseCoverLetterSaveError, setBaseCoverLetterSaveError] = useState<ApiError | null>(null);

  const [applyingLoading, applyingError, applyForJob] = useActionApi({
    apiMethod: Api.applyForHireJob,
    onSuccess: useCallback((res: { jobApplicationId?: number }) => {
      setJob((prev) => ({
        ...prev,
        isApplied: 1,
        applicationId: res?.jobApplicationId ?? prev.applicationId,
      }));
      setApplicationSuccessfulPopupOpened(true);
    }, [])
  })
  const [applyingWithCoverLetterLoading, applyingWithCoverLetterError, applyForJobWithCoverLetter] = useActionApi({
    apiMethod: Api.applyForHireJobWithCoverLetter,
    onSuccess: useCallback((res: { jobApplicationId?: number }) => {
      setJob((prev) => ({
        ...prev,
        isApplied: 1,
        applicationId: res?.jobApplicationId ?? prev.applicationId,
      }));
      setApplicationSuccessfulPopupOpened(true);
    }, [])
  })
  const [withdrawingLoading, withdrawingError, withdrawJob] = useActionApi({
    apiMethod: Api.withDrawForHireJob,
    onSuccess: useCallback(() => {
      setJob((prev) => ({ ...prev, isApplied: 0 }));
      setApplicationWithdrawnPopupOpened(true)
    }, [history])
  })

  const [noResumePopupOpened, setNoResumePopupOpened] = useState(false)
  const [withdrawPopupOpened, setWithdrawPopupOpened] = useState(false)
  const [applicationSuccessfulPopupOpened, setApplicationSuccessfulPopupOpened] = useState(false)
  const [applicationWithdrawnPopupOpened, setApplicationWithdrawnPopupOpened] = useState(false)
  const [jobExpiredPopupOpened, setJobExpiredPopupOpened] = useState(false)

  // Check if job is expired (start date has passed)
  const jobIsExpired = useMemo(() => isJobExpired(job.startDate), [job.startDate]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setUserLoading(true);
        const user = await Api.getUser();
        setHygienistUser(user);
      } catch (error) {
        console.error("Failed to fetch user data", error);
      } finally {
        setUserLoading(false);
      }
    };

    fetchUser();
  }, []);

  const onApplyButtonClick = async () => {
    // Prevent applying to expired jobs
    if (jobIsExpired) {
      console.warn('[JobPostDetailsPage] Cannot apply to job - start date has passed.');
      setJobExpiredPopupOpened(true);
      return;
    }

    if (!hygienistUser?.hygienist) {
      console.error('[JobPostDetailsPage] Hygienist user data not loaded. Cannot proceed with application.');
      setNoResumePopupOpened(true);
      return;
    }
    const { hireResumeUrl, baseCoverLetter, type: hygienistType } = hygienistUser.hygienist;
    
    // Check if cover letter modal should be shown
    if (includeCoverLetter && !coverLetterPrepared) {
      console.log('[JobPostDetailsPage] Opening cover letter modal for job application.');
      setShowCoverLetterModal(true);
      setTemporaryCoverLetter(baseCoverLetter || null);  // Initialize with base or null
      return;
    }
    
    // If cover letter is included and already prepared, submit the application
    if (includeCoverLetter && coverLetterPrepared && temporaryCoverLetter) {
      console.log('[JobPostDetailsPage] Submitting application with prepared cover letter');
      await applyForJobWithCoverLetter({
        hireJobId: job.id, 
        coverLetter: temporaryCoverLetter
      });
      return;
    }

    console.log('[JobPostDetailsPage] Applying for job without cover letter.');

    if (typeof hygienistType === 'number') {
      if (hygienistType < 2) {
        console.log('[JobPostDetailsPage] Applying for Hygienist/Assistant - bypassing resume check.');
        await applyForJob(job.id);
      } else {
        console.log('[JobPostDetailsPage] Applying for Admin/Dentist - checking for resume.');
        if (hireResumeUrl) {
          await applyForJob(job.id);
        } else {
          setNoResumePopupOpened(true);
        }
      }
    } else {
      console.error('[JobPostDetailsPage] Hygienist user data or type is undefined. Cannot proceed with application.');
      setNoResumePopupOpened(true);
    }
  }

  const handleCoverLetterComplete = async (coverLetter: string, saveAsBase?: boolean) => {
    setShowCoverLetterModal(false);
    setCoverLetterProcessing(true);

    setTemporaryCoverLetter(coverLetter);
    setCoverLetterPrepared(true);

    try {
      if (saveAsBase) {
        console.log('[JobPostDetailsPage] Updating base cover letter');
        await Api.updateHygienistAccount({ baseCoverLetter: coverLetter });
        const updatedUser = await Api.getUser();
        setHygienistUser(updatedUser);
      }
    } catch (error) {
      console.error('[JobPostDetailsPage] Error updating base cover letter:', error);
    }

    try {
      console.log('[JobPostDetailsPage] Submitting application with prepared cover letter');
      await applyForJobWithCoverLetter({
        hireJobId: job.id,
        coverLetter,
      });
    } catch (error) {
      console.error('[JobPostDetailsPage] Error submitting application with cover letter:', error);
    } finally {
      setCoverLetterProcessing(false);
    }
  };

  const handleWritingAssistCoverLetterComplete = async (selection: WritingAssistSaveSelection) => {
    setCoverLetterProcessing(true);
    setBaseCoverLetterSaveError(null);
    try {
      // The application create is the authoritative save/gate boundary. Do it
      // before the optional profile write so a coach cannot partially change
      // the Base Cover Letter or lead to a duplicate application retry.
      const outcome = await submitWritingAssistApplication({
        hireJobId: job.id,
        selection,
        createApplication: payload => Api.applyForHireJobWithCoverLetter(payload),
        updateBaseCoverLetter: payload => Api.updateHygienistAccount(payload),
      });
      const response = outcome.application;
      setTemporaryCoverLetter(selection.text);
      setCoverLetterPrepared(true);
      setJob(previous => ({
        ...previous,
        isApplied: 1,
        applicationId: response?.jobApplicationId ?? previous.applicationId,
      }));
      setShowCoverLetterModal(false);
      setApplicationSuccessfulPopupOpened(true);

      if (selection.saveAsBase) {
        if (outcome.baseCoverLetterUpdated) {
          // The application and optional Base Cover Letter writes are already
          // complete. Refreshing local account state is best-effort and must not
          // turn that successful transaction into a contradictory save error.
          void Api.getUser()
            .then(updatedUser => setHygienistUser(updatedUser))
            .catch(() => undefined);
        } else {
          // Never retry the already-created application. Report the secondary
          // profile-write outcome honestly and leave the successful application intact.
          setBaseCoverLetterSaveError(new ApiError(
            500,
            "Your application was submitted, but we couldn't update your Base Cover Letter. You can update it from your profile.",
          ));
        }
      }
      return response;
    } finally {
      setCoverLetterProcessing(false);
    }
  };

  const onWithdrawButtonClick = () => {
    setWithdrawPopupOpened(true);
  }

  const onNoResumePopupClose = () => {
    setNoResumePopupOpened(false);
  }
  const onWithdrawPopupClose = () => {
    setWithdrawPopupOpened(false);
  }
  const onApplicationSuccessfulPopupClose = () => {
    setApplicationSuccessfulPopupOpened(false);
  }
  const onApplicationWithdrawnPopupClose = () => {
    setApplicationWithdrawnPopupOpened(false);
  }

  const onResumeUploaded = async () => {
    if (!hygienistUser?.hygienist) return;
    onNoResumePopupClose();
    const coverLetter = includeCoverLetter ? hygienistUser.hygienist.baseCoverLetter : null;
    if (includeCoverLetter) {
      await applyForJobWithCoverLetter({hireJobId: job.id, coverLetter});
    } else {
      await applyForJob(job.id);
    }
  }

  const onApplicationWithdraw = async () => {
    onWithdrawPopupClose();
    const applicationId = Number(job.applicationId);
    if (!Number.isFinite(applicationId) || applicationId <= 0) {
      console.error('[JobPostDetailsPage] Missing applicationId — cannot withdraw');
      return;
    }
    await withdrawJob({ applicationId });
  }

  const onApplicationAppliedConfirm = () => {
    onApplicationSuccessfulPopupClose();
    history.replace('jobPostsList');
  }
  const onApplicationWithdrawnConfirm = () => {
    setApplicationWithdrawnPopupOpened(false);
    history.replace('jobPostsList');
  }

  // Check if we're returning from cover letter creation
  useEffect(() => {
    const locationState = history.location.state as any;
    if (locationState?.returnedFromCoverLetterCreation && locationState?.job?.id === job.id) {
      // Re-fetch user to get updated baseCoverLetter
      const refreshUser = async () => {
        try {
          const user = await Api.getUser();
          setHygienistUser(user);
          // Ensure checkbox is still checked
          setIncludeCoverLetter(true);
        } catch (error) {
          console.error("Failed to refresh user data", error);
        }
      };
      refreshUser();
      
      // Clear the return state to prevent re-triggering
      history.replace(history.location.pathname, { job });
    }
  }, [history, job.id]);

  if (userLoading) {
    return <FixedLoader loading={true} />
  }

  // T2-6162 / T2-6194: use the shared TalentNavbar (which always renders the
  // hamburger menu) instead of TopBarRedesigned so the burger is present on the
  // applied-job details screen and the back button matches every other Talent page.
  const navHygienist = hygienistUser?.hygienist;
  const navIsHygOrAsst = navHygienist?.type === HYGIENIST_TYPES.HYGIENIST
    || navHygienist?.type === HYGIENIST_TYPES.ASSISTANT;

  return (
    <>
      <TalentPageLayout
        navbar={(
          <TalentNavbar
            firstName={navHygienist?.firstName || ''}
            lastName={navHygienist?.lastName || ''}
            photoUrl={navHygienist?.photoUrl || undefined}
            isHygOrAsst={navIsHygOrAsst}
            instabookActivated={(navHygienist as any)?.InstabookActivated === 1}
            isTestAccount={(navHygienist as any)?.isTest === 1}
            showAddShiftButton={false}
            showJobBoardButton={false}
            pageTitle='Job Details'
          />
        )}
      >
      <TalentBackButton onBack={() => history.goBack()} background="#F8F9FB" />
      <TalentPageTitle title="Job Details" />
      <HygienistJobDetails
        job={job}
        includeCoverLetter={includeCoverLetter}
        onIncludeCoverLetterChange={(checked: boolean) => {
          setIncludeCoverLetter(checked);
          // Reset cover letter state when unchecked
          if (!checked) {
            setCoverLetterPrepared(false);
            setTemporaryCoverLetter(null);
          }
        }}
        onApply={onApplyButtonClick}
        onWithdraw={onWithdrawButtonClick}
        isExpired={jobIsExpired}
      />
      </TalentPageLayout>
      <Popup
        opened={noResumePopupOpened}
        onClose={onNoResumePopupClose}
      >
        <NoResumeActions onCancel={onNoResumePopupClose} onResumeUploaded={onResumeUploaded}/>
      </Popup>
      <WithdrawActions
        isOpen={withdrawPopupOpened}
        onCancel={onWithdrawPopupClose}
        onApplicationWithdraw={onApplicationWithdraw}
      />
      {/* T2-6088: branded success modal with full JobApplySuccess guidance copy */}
      <JobBoardApplySuccessModal
        isOpen={applicationSuccessfulPopupOpened}
        user={hygienistUser}
        onClose={onApplicationSuccessfulPopupClose}
        onBackToJobPostings={onApplicationAppliedConfirm}
      />
      <SuccessAction
        isOpen={applicationWithdrawnPopupOpened}
        title="Application Withdrawn"
        description="Your application has been successfully withdrawn."
        onConfirm={onApplicationWithdrawnConfirm}
        onClose={onApplicationWithdrawnPopupClose}
      />
      <SuccessAction
        isOpen={jobExpiredPopupOpened}
        title="Job No Longer Available"
        description="This job posting has expired and is no longer accepting applications. Please browse other available positions."
        onConfirm={() => {
          setJobExpiredPopupOpened(false);
          history.replace('jobPostsList');
        }}
        onClose={() => setJobExpiredPopupOpened(false)}
      />
      {showCoverLetterModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 1000,
          overflow: 'auto'
        }}>
          <CoverLetter
            coverLetter={temporaryCoverLetter || hygienistUser?.hygienist?.baseCoverLetter}
            name={hygienistUser?.hygienist?.firstName}
            jobTitle={job.title}  // Pass job context
            practiceName={job.location?.practiceName}
            jobId={job.id}
            isOpen={showCoverLetterModal}
            onClose={() => setShowCoverLetterModal(false)}
            onSaveCoverLetter={handleCoverLetterComplete}
            onSaveWritingAssistCoverLetter={handleWritingAssistCoverLetterComplete}
            onWritingAssistSaveError={error => setBaseCoverLetterSaveError(
              error instanceof ApiError
                ? error
                : new ApiError(500, error instanceof Error ? error.message : 'Could not submit your application.'),
            )}
          />
        </div>
      )}
      <FixedLoader loading={applyingLoading || withdrawingLoading || userLoading || applyingWithCoverLetterLoading || coverLetterProcessing}/>
      <FixedError
        error={baseCoverLetterSaveError || applyingError || withdrawingError || applyingWithCoverLetterError}
        onDismiss={() => setBaseCoverLetterSaveError(null)}
      />
    </>
  )
}
